/**
 * Combined height (px) of the fixed two-tier toolbar (AppBar + Header) per
 * breakpoint. AppBar steps `h-11 md:h-12` (44/44/48px); Header steps `h-14
 * sm:h-16 md:h-20` (56/64/80px), so the combined bar grows across the mobile
 * range. The bar classes live in {@link APP_BAR_HEIGHT_CLASS}/
 * {@link HEADER_HEIGHT_CLASS} and the matching content padding in
 * {@link CONTENT_OFFSET_CLASS} — keep all three in sync.
 *
 * The bars only hide once the page scrolls past this, and the mobile sticky
 * sub-header offsets itself by this much while the bars show.
 */
export const COMBINED_BAR_HEIGHT = {
  base: 100,
  sm: 108,
  md: 128,
} as const;

/**
 * Combined toolbar height for the active breakpoint, given whether the viewport
 * is at/above the sm and md breakpoints. `md` wins over `sm`; below both the
 * base height applies.
 */
export const resolveCombinedBarHeight = (isSm: boolean, isMd: boolean): number => {
  if (isMd) return COMBINED_BAR_HEIGHT.md;
  if (isSm) return COMBINED_BAR_HEIGHT.sm;
  return COMBINED_BAR_HEIGHT.base;
};

/**
 * Tailwind height utilities for the two toolbar bars, defined once and shared by
 * the live {@link AppBar}/{@link Header} and the static `ToolbarFallback`, so the
 * rendered bar height stays in lockstep with {@link COMBINED_BAR_HEIGHT} and
 * {@link CONTENT_OFFSET_CLASS} instead of being copied per component.
 */
export const APP_BAR_HEIGHT_CLASS = 'h-11 md:h-12';
export const HEADER_HEIGHT_CLASS = 'h-14 sm:h-16 md:h-20';

/**
 * Top padding that pushes page content below the fixed toolbar. Numerically
 * equals {@link COMBINED_BAR_HEIGHT} per breakpoint (`pt-25`=100, `pt-27`=108,
 * `pt-32`=128px) and is the single offset used by the layout and both fallbacks.
 */
export const CONTENT_OFFSET_CLASS = 'pt-25 sm:pt-27 md:pt-32';

/** Minimum scroll delta (px) that flips visibility — suppresses jitter. */
export const SCROLL_DEADZONE = 2;

/** Tailwind `sm` breakpoint — Header steps up to `h-16` here. */
export const SM_MEDIA_QUERY = '(min-width: 640px)';

/** Tailwind `md` breakpoint — AppBar/Header step up to `h-12`/`h-20` here. */
export const MD_MEDIA_QUERY = '(min-width: 768px)';

/**
 * Breakpoint at and above which the toolbar is always visible (Tailwind `lg`).
 * Scroll-hide and the mobile sticky offset are disabled here.
 */
export const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
